import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasLogisticsAccess } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, DownloadSimple } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

export default function SupplyRequests() {
  const { user } = useAuth();
  const canDecide = hasLogisticsAccess(user);
  const [list, setList] = useState([]);
  // Live catalog fetched from /inventory. Members pick items by name from a
  // dropdown so a request always ties back to a real SKU on the shelf, and
  // the request PDF stays consistent with the inventory ledger.
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [items, setItems] = useState([{ name: "", notes: "" }]);
  const [justification, setJustification] = useState("");
  const [priority, setPriority] = useState("normal");
  const [supervisorNote, setSupervisorNote] = useState("");
  const [emailTo, setEmailTo] = useState("");

  async function load() { const { data } = await api.get("/supply-requests/"); setList(data); }
  async function loadCatalog() {
    try {
      const { data } = await api.get("/inventory/");
      // Sort by category → name so the dropdown groups similar SKUs together.
      const arr = [...(data || [])].sort((a, b) =>
        String(a.category || "").localeCompare(String(b.category || "")) ||
        String(a.name || "").localeCompare(String(b.name || ""))
      );
      setCatalog(arr);
    } catch { setCatalog([]); }
  }
  useEffect(() => { load(); loadCatalog(); }, []);

  function addRow() { setItems([...items, { name: "", notes: "" }]); }
  function removeRow(i) { setItems(items.filter((_, idx) => idx !== i)); }

  async function submit(e) {
    e.preventDefault();
    const clean = items.filter((i) => i.name.trim());
    if (clean.length === 0) { toast.error("Add at least one item"); return; }
    try {
      await api.post("/supply-requests/", { items: clean.map((i) => ({ name: i.name, notes: i.notes })), justification, priority });
      toast.success("Request sent to Commanding Officer / Admin");
      setOpenCreate(false); setItems([{ name: "", notes: "" }]); setJustification(""); setPriority("normal");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function decide(approved) {
    try {
      const res = await api.post(`/supply-requests/${selected.id}/decision`, { approved, supervisor_note: supervisorNote, email_to: emailTo || undefined });
      toast.success(approved ? `Approved${res.data.email_sent ? " · emailed" : ""}` : "Rejected");
      setSelected(null); setSupervisorNote(""); setEmailTo(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  return (
    <AppLayout title="Supply Requests" subtitle="Submit requests; approved requests are exported to PDF and emailed." actions={
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 text-white" data-testid="supply-new"><Plus size={14} weight="bold" /> New Request</Button></DialogTrigger>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>New Supply Request</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-7">
                    <Label className="tab-tag">Item</Label>
                    {catalog.length > 0 ? (
                      <select
                        required
                        value={it.name}
                        onChange={(e) => { const a = [...items]; a[i].name = e.target.value; setItems(a); }}
                        className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
                        data-testid={`supply-item-${i}`}
                      >
                        <option value="">Select an item…</option>
                        {catalog.map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                            {c.category ? ` · ${c.category}` : ""}
                            {typeof c.quantity === "number" ? ` (on-hand: ${c.quantity})` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        data-testid={`supply-item-${i}`}
                        required
                        value={it.name}
                        onChange={(e) => { const a = [...items]; a[i].name = e.target.value; setItems(a); }}
                        className="rounded-sm"
                        placeholder="Item name"
                      />
                    )}
                  </div>
                  <div className="col-span-4"><Label className="tab-tag">Notes</Label><Input data-testid={`supply-itemnote-${i}`} value={it.notes} onChange={(e) => { const a = [...items]; a[i].notes = e.target.value; setItems(a); }} className="rounded-sm" placeholder="qty / brand / size" /></div>
                  <Button type="button" size="icon" variant="ghost" className="text-red-600" onClick={() => removeRow(i)} data-testid={`supply-row-del-${i}`}><Trash size={14} /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addRow} className="rounded-sm" data-testid="supply-addrow"><Plus size={12} /> Add line</Button>
              {catalog.length === 0 && (
                <div className="text-[11px] text-amber-700 mt-1">Inventory catalog is empty — items can be typed in manually until stock is added.</div>
              )}
            </div>
            <div><Label className="tab-tag">Justification</Label><Textarea className="rounded-sm" data-testid="supply-justification" value={justification} onChange={(e) => setJustification(e.target.value)} /></div>
            <div><Label className="tab-tag">Priority</Label>
              <select className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="supply-priority"><option value="normal">Normal</option><option value="urgent">Urgent</option></select>
            </div>
            <DialogFooter><Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="supply-submit">Submit Request</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    }>
      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Submitted</th><th className="px-4 py-2">Requester</th><th className="px-4 py-2">Items</th><th className="px-4 py-2">Priority</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (<tr><td colSpan={6} className="px-4 py-6 text-slate-500">No supply requests yet.</td></tr>) :
              list.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`supply-row-${r.id}`}>
                  <td className="px-4 py-2 mono">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2">{r.requester_name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.items.length} item(s)</td>
                  <td className="px-4 py-2 mono uppercase">{r.priority}</td>
                  <td className="px-4 py-2"><Badge className={`rounded-sm border ${STATUS_STYLE[r.status]}`}>{r.status.toUpperCase()}</Badge></td>
                  <td className="px-4 py-2 text-right">
                    {r.status !== "pending" && (
                      <a onClick={(e) => e.stopPropagation()} href={`${API_BASE}/supply-requests/${r.id}/pdf`} target="_blank" rel="noreferrer" className="text-xs underline text-slate-700" data-testid={`supply-pdf-${r.id}`}>PDF</a>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-[460px] sm:max-w-[480px]" data-testid="supply-drawer">
          {selected && (
            <>
              <SheetHeader><SheetTitle>Supply Request</SheetTitle></SheetHeader>
              <div className="mt-2 text-xs text-slate-500 mono">{selected.id}</div>
              <div className="mt-4 space-y-2 text-sm">
                <div><span className="tab-tag text-slate-500">Requester</span> {selected.requester_name}</div>
                <div><span className="tab-tag text-slate-500">Priority</span> <span className="mono uppercase">{selected.priority}</span></div>
                <div><span className="tab-tag text-slate-500">Submitted</span> <span className="mono">{new Date(selected.created_at).toLocaleString()}</span></div>
                <div><span className="tab-tag text-slate-500">Justification</span><div className="text-slate-800">{selected.justification || "—"}</div></div>
              </div>
              <div className="mt-4 border-t border-slate-200 pt-3">
                <div className="tab-tag text-slate-500 mb-2">Items</div>
                <ul className="text-sm space-y-1">
                  {selected.items.map((it, i) => (<li key={i} className="border-b border-slate-100 py-1"><span>{it.name}{it.notes ? <span className="text-slate-500"> · {it.notes}</span> : null}</span></li>))}
                </ul>
              </div>

              {canDecide && selected.status === "pending" && (
                <div className="mt-4 border-t border-slate-200 pt-3 space-y-2">
                  <Label className="tab-tag">Supervisor note</Label>
                  <Textarea className="rounded-sm" value={supervisorNote} onChange={(e) => setSupervisorNote(e.target.value)} data-testid="supply-supnote" />
                  <Label className="tab-tag">Email to (purchasing)</Label>
                  <Input placeholder="leave blank for default" className="rounded-sm" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="supply-emailto" />
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button onClick={() => decide(true)} className="rounded-sm bg-emerald-700 text-white" data-testid="supply-approve">Approve · PDF + Email</Button>
                    <Button variant="outline" onClick={() => decide(false)} className="rounded-sm border-red-300 text-red-600 hover:bg-red-50" data-testid="supply-reject">Reject</Button>
                  </div>
                </div>
              )}
              {selected.status !== "pending" && (
                <div className="mt-4 border-t border-slate-200 pt-3">
                  <Button asChild className="w-full rounded-sm bg-slate-900 text-white" data-testid="supply-dl-pdf"><a href={`${API_BASE}/supply-requests/${selected.id}/pdf`} target="_blank" rel="noreferrer"><DownloadSimple size={14} weight="bold" className="mr-1" /> Download PDF</a></Button>
                  {selected.email_sent && <div className="mt-2 text-xs text-emerald-700">Emailed to {selected.email_recipient || "purchasing"}.</div>}
                  {selected.email_result && !selected.email_sent && (
                    <div className="mt-2 text-xs text-amber-700">Email not sent: {selected.email_result.error || "Resend API key not configured."}</div>
                  )}
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
